import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api, forgetAdminTenantSlug, rememberAdminTenantSlug } from "@/lib/api";
import {
  apiErrorCode,
  isTenantSuspendedError,
  OWNER_TENANT_MISMATCH_CODE,
  TENANT_SELECTION_REQUIRED_CODE,
  tenantChoices,
} from "@/lib/apiErrors";
import { useT } from "@/lib/LanguageContext";
import { Loader2 } from "lucide-react";
import { adminHref, adminLoginHref } from "./adminPath";

// Refusal of a platform operator who reaches a practice-scoped route with no practice
// named (backend/server.py require_admin, tenant_roles). Not in lib/apiErrors.js: this
// door is its only reader.
const TENANT_CONTEXT_REQUIRED_CODE = "TENANT_CONTEXT_REQUIRED";

/**
 * ProtectedRoute — garde la zone /admin.
 * Multi-tenant : tout PROPRIÉTAIRE de tenant (ou super-admin) accède à SON dashboard.
 * Le slug du tenant authentifié est résolu via /saas/my-tenant et propagé à l'API
 * (header X-Tenant-Slug) pour le branding + l'isolation des données.
 */
const ProtectedRoute = ({ children, requireAdmin = true }) => {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const t = useT("admin");
  const [resolving, setResolving] = React.useState(true);
  const [allowed, setAllowed] = React.useState(false);
  const [suspended, setSuspended] = React.useState(false);
  // The practices this account owns, when it owns SEVERAL and has named none: the
  // backend refuses to guess (TENANT_SELECTION_REQUIRED) and hands them over, because
  // guessing is what bound a practitioner to the wrong practice (F-01).
  const [choices, setChoices] = React.useState([]);
  const [attempt, setAttempt] = React.useState(0);
  const [noContext, setNoContext] = React.useState(false);

  React.useEffect(() => {
    if (loading) return;
    if (!user) { setResolving(false); return; }
    let cancelled = false;
    // Set by the stale-slug branch below: that outcome is not a RESULT, it is a second
    // attempt, so the spinner must stay up rather than flash the access-denied card
    // between the two calls.
    let retrying = false;
    (async () => {
      try {
        const { data } = await api.get("/saas/my-tenant");
        if (cancelled) return;
        // The store keeps it for the bare /admin tree only: under /t/{slug}/admin the
        // path names the practice and nothing is remembered (lib/api.js, rule 1).
        rememberAdminTenantSlug(data?.slug);
        setAllowed(true);
      } catch (e) {
        if (cancelled) return;
        const code = apiErrorCode(e);
        // The remembered slug (localStorage, sent as X-Tenant-Slug) names a practice
        // this account does not own — another account signed in on this browser, or the
        // practice was renamed. It is a CACHE, and a cache the server rejects must be
        // dropped, not obeyed: clear it and ask once more, unheadered.
        if (code === OWNER_TENANT_MISMATCH_CODE && attempt === 0) {
          retrying = true;
          forgetAdminTenantSlug();
          setAttempt(1);
          return;
        }
        // Several practices, none named. Nothing to resolve — the owner chooses.
        if (code === TENANT_SELECTION_REQUIRED_CODE) {
          setChoices(tenantChoices(e));
          return;
        }
        // 403 TENANT_SUSPENDED is NOT "no tenant linked": the practice exists and its
        // owner is barred (a platform operator suspended it — backend/tenant_middleware
        // .tenant_login_blocked). It needs its own answer for two reasons: falling into
        // the branch below would boot the whole dashboard for a suspended owner who also
        // happens to be a global admin, and everyone else would read "not linked to any
        // practitioner space", which is false and unactionable.
        //
        // isTenantSuspendedError() (lib/apiErrors.js) is the shared classifier
        // TenantContext.jsx and TenantSite.jsx already use — it recognises BOTH the bare
        // string `detail` (what tenant_middleware/saas_routes send today) and the
        // structured `{code: "TENANT_SUSPENDED"}` shape the rest of the API's refusals
        // use, so this door does not silently stop detecting suspension if a backend path
        // ever emits the object form. A hand-rolled `=== "TENANT_SUSPENDED"` string
        // compare would only catch the first.
        if (isTenantSuspendedError(e)) {
          setSuspended(true);
          return;   // the `finally` below still clears `resolving`
        }
        // A platform operator who named no practice is refused 400
        // TENANT_CONTEXT_REQUIRED: show an explicit "no practice selected" state instead
        // of a dashboard whose every call fails. Any other refusal is access denied.
        if (user.can_enter_practices && code === TENANT_CONTEXT_REQUIRED_CODE) {
          setNoContext(true);
        } else {
          setAllowed(false);
        }
      } finally {
        if (!cancelled && !retrying) setResolving(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, attempt]);

  if (loading || (user && resolving)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-eh-bg">
        <Loader2 className="animate-spin text-eh-sage-deep" size={28} />
      </div>
    );
  }

  // The page being protected travels to the login page as its return target
  // (adminPath.adminLoginHref), so a signed-out practitioner following a link into the
  // console (an email's room link) lands back on that page once signed in. The ROUTER's
  // location, not window.location: it is the address THIS element was rendered for, so a
  // re-render of the outgoing route after the redirect cannot name the login page itself.
  if (!user) {
    return <Navigate to={adminLoginHref(`${location.pathname}${location.search}${location.hash}`)} replace />;
  }

  // Several practices, none designated: the owner picks one, it becomes the slug every
  // later request carries, and the bootstrap runs again. No default — a default is
  // exactly what silently crossed two practices' data.
  if (choices.length > 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-eh-bg px-6">
        <div className="max-w-md w-full text-center" data-testid="admin-choose-practice">
          <h2 className="font-display text-3xl text-eh-ink">{t("choosePractice.title")}</h2>
          <p className="mt-3 text-eh-muted">{t("choosePractice.body")}</p>
          <div className="mt-6 flex flex-col gap-2">
            {choices.map((c) => (
              <button
                key={c.tenant_id}
                data-testid={`admin-choose-practice-${c.slug}`}
                onClick={() => {
                  rememberAdminTenantSlug(c.slug);
                  setChoices([]);
                  setResolving(true);
                  setAttempt((n) => n + 1);
                }}
                className="px-6 py-2.5 rounded-2xl border border-eh-border-soft bg-eh-cream text-eh-ink text-sm hover:bg-eh-bg"
              >
                {c.name || c.slug}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const switchAccount = async () => { await logout(); window.location.href = adminHref("/admin/login"); };

  // Suspended by the platform: the session may still be valid (the /admin shell is what
  // is barred), so this is a dead end by design — the only way out is support, or signing
  // in as somebody else. Checked BEFORE the access-denied card, which would otherwise
  // give the wrong reason for the same blank screen.
  if (suspended) {
    return (
      <RefusalCard
        testId="admin-suspended" switchTestId="admin-suspended-switch-account"
        title={t("suspended.title")} body={t("suspended.body")} hint={t("suspended.hint")}
        switchLabel={t("accessDenied.switchAccount")} onSwitch={switchAccount}
      />
    );
  }

  // Explicit no-practice-selected state for a platform operator who named no practice.
  if (noContext) {
    return (
      <RefusalCard
        testId="admin-no-context" switchTestId="admin-nocontext-switch-account"
        title={t("noContext.title")} body={t("noContext.body")} hint={t("noContext.hint")}
        switchLabel={t("accessDenied.switchAccount")} onSwitch={switchAccount}
      />
    );
  }

  if (requireAdmin && !allowed) {
    // A Finance, Support or Read-only team member (GET /auth/me `is_platform_operator`, and
    // not `can_enter_practices`) is refused by the Super-Admin rule (backend operator_entry),
    // not because their address owns no practice: the practitioner wording would send them
    // looking for a practice they were never meant to have.
    if (user.is_platform_operator && !user.can_enter_practices) {
      return (
        <RefusalCard
          testId="admin-entry-reserved" switchTestId="admin-entry-reserved-switch-account"
          title={t("entryReserved.title")}
          body={t("entryReserved.body", { email: user.email })}
          hint={t("entryReserved.hint")}
          switchLabel={t("accessDenied.switchAccount")} onSwitch={switchAccount}
        />
      );
    }
    return (
      <RefusalCard
        testId="admin-access-denied" switchTestId="admin-denied-switch-account"
        title={t("accessDenied.title")}
        body={t("accessDenied.body", { email: user.email })}
        hint={t("accessDenied.hint")}
        switchLabel={t("accessDenied.switchAccount")} onSwitch={switchAccount}
      />
    );
  }

  return children;
};

/** A dead end of the /admin door: why, what to do, and the only way out (another account). */
function RefusalCard({ testId, switchTestId, title, body, hint, switchLabel, onSwitch }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-eh-bg px-6">
      <div className="max-w-md text-center" data-testid={testId}>
        <h2 className="font-display text-3xl text-eh-ink">{title}</h2>
        <p className="mt-3 text-eh-muted">{body}</p>
        <p className="mt-2 text-sm text-eh-muted">{hint}</p>
        <button
          data-testid={switchTestId}
          onClick={onSwitch}
          className="mt-6 px-6 py-2.5 rounded-full bg-eh-ink text-white text-sm hover:opacity-90"
        >
          {switchLabel}
        </button>
      </div>
    </div>
  );
}

export default ProtectedRoute;
